import { CognitoUser } from 'amazon-cognito-identity-js';

type CognitoUserProto = CognitoUser & {
  Session: string;
  username: string;
  deviceKey: string | null;
  client: { request: (operation: string, params: object, callback: (err: Error | null, data?: any) => void) => void };
  getUserContextData: () => object | undefined;
  getCognitoUserSession: (authResult: object) => object;
  cacheTokens: () => void;
  signInUserSession: object;
  getDeviceResponse: (callback: object) => void;
};

let patched = false;

export function applyEmailOtpPatch(): void {
  if (patched) return;
  patched = true;

  const proto = CognitoUser.prototype as CognitoUserProto & {
    authenticateUserInternal: (
      dataAuthenticate: { ChallengeName: string; ChallengeParameters: object; Session: string },
      authenticationHelper: object,
      callback: {
        emailOtpRequired?: (challengeName: string, challengeParameters: object) => void;
        mfaRequired?: (challengeName: string, challengeParameters: object) => void;
        onFailure: (err: Error) => void;
      }
    ) => void;
    sendMFACode: (
      confirmationCode: string,
      callback: object,
      mfaType?: string,
      clientMetadata?: object
    ) => void;
    sendMFASelectionAnswer: (answerChallenge: string, callback: object) => void;
  };

  const originalAuthenticateUserInternal = proto.authenticateUserInternal;
  proto.authenticateUserInternal = function authenticateUserInternal(dataAuthenticate, authenticationHelper, callback) {
    const challengeName = dataAuthenticate.ChallengeName;
    const challengeParameters = dataAuthenticate.ChallengeParameters;

    if (challengeName === 'EMAIL_OTP' || challengeName === 'EMAIL_MFA') {
      this.Session = dataAuthenticate.Session;
      if (callback.emailOtpRequired) {
        return callback.emailOtpRequired(challengeName, challengeParameters);
      }
      if (callback.mfaRequired) {
        return callback.mfaRequired(challengeName, challengeParameters);
      }
      return callback.onFailure(new Error(`${challengeName} is not supported`));
    }

    return originalAuthenticateUserInternal.call(this, dataAuthenticate, authenticationHelper, callback);
  };

  const originalSendMFACode = proto.sendMFACode;
  proto.sendMFACode = function sendMFACode(confirmationCode, callback, mfaType, clientMetadata) {
    const mfaTypeSelection = mfaType || 'SMS_MFA';
    if (mfaTypeSelection !== 'EMAIL_OTP' && mfaTypeSelection !== 'EMAIL_MFA') {
      return originalSendMFACode.call(this, confirmationCode, callback, mfaType, clientMetadata);
    }

    const user = this as CognitoUserProto;
    const challengeResponses: Record<string, string> = { USERNAME: user.username };
    if (mfaTypeSelection === 'EMAIL_OTP') {
      challengeResponses.EMAIL_OTP_CODE = confirmationCode;
    } else {
      challengeResponses.EMAIL_MFA_CODE = confirmationCode;
    }
    if (user.deviceKey != null) {
      challengeResponses.DEVICE_KEY = user.deviceKey;
    }

    const jsonReq: Record<string, unknown> = {
      ChallengeName: mfaTypeSelection,
      ChallengeResponses: challengeResponses,
      ClientId: (user as any).pool.getClientId(),
      Session: user.Session,
      ClientMetadata: clientMetadata,
    };
    const userContextData = user.getUserContextData();
    if (userContextData) {
      jsonReq.UserContextData = userContextData;
    }

    user.client.request('RespondToAuthChallenge', jsonReq, (err, dataAuthenticate) => {
      if (err) {
        return (callback as { onFailure: (e: Error) => void }).onFailure(err);
      }
      if (dataAuthenticate.ChallengeName === 'DEVICE_SRP_AUTH') {
        user.getDeviceResponse(callback);
        return;
      }
      user.signInUserSession = user.getCognitoUserSession(dataAuthenticate.AuthenticationResult) as any;
      user.cacheTokens();
      return (callback as { onSuccess: (session: object) => void }).onSuccess(user.signInUserSession);
    });
  };

  const originalSendMFASelectionAnswer = proto.sendMFASelectionAnswer;
  proto.sendMFASelectionAnswer = function sendMFASelectionAnswer(answerChallenge, callback) {
    if (answerChallenge !== 'EMAIL_OTP' && answerChallenge !== 'EMAIL_MFA') {
      return originalSendMFASelectionAnswer.call(this, answerChallenge, callback);
    }

    const user = this as CognitoUserProto;
    const challengeResponses = {
      USERNAME: user.username,
      ANSWER: answerChallenge,
    };
    const jsonReq: Record<string, unknown> = {
      ChallengeName: 'SELECT_MFA_TYPE',
      ChallengeResponses: challengeResponses,
      ClientId: (user as any).pool.getClientId(),
      Session: user.Session,
    };
    const userContextData = user.getUserContextData();
    if (userContextData) {
      jsonReq.UserContextData = userContextData;
    }

    user.client.request('RespondToAuthChallenge', jsonReq, (err, data) => {
      if (err) {
        return (callback as { onFailure: (e: Error) => void }).onFailure(err);
      }
      user.Session = data.Session;
      const cb = callback as {
        emailOtpRequired?: (challengeName: string, challengeParameters: object) => void;
        mfaRequired?: (challengeName: string, challengeParameters: object) => void;
      };
      if (cb.emailOtpRequired) {
        return cb.emailOtpRequired(data.ChallengeName, data.ChallengeParameters);
      }
      if (cb.mfaRequired) {
        return cb.mfaRequired(data.ChallengeName, data.ChallengeParameters);
      }
    });
  };
}
